/* eslint-disable @typescript-eslint/no-unused-vars */
import i18nInst from 'src/i18n';
import {
  addEmails,
  addUrlToList,
  checkActionByType,
  checkEmailFormat,
  checkNotification,
  checkToken,
  removeHeader,
  removeItem,
  removeProtocol,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
  showAddUrlExceedListLimitNotification,
  showAddUrlsErrNotification,
} from 'src/pages/Policy/utils/fns';
import {
  ApprovedHeader,
  ExchangeOnlinePolicyItem,
  Notification,
  PolicyFilters,
  WebThreatProtectionFilter,
} from 'src/pages/Policy/utils/types';

import {
  wtpActionDataSelector,
  wtpApprovedHeaderListSelector,
} from './selectors';
import { PolicyTypeEnum } from '@Policy/utils/constants';

export function createWebThreatProtectionSlice(
  setPolicy: any,
  getPolicy: any,
  getAll: any
) {
  const { t } = i18nInst;
  return {
    setWtpState: (payload: Partial<WebThreatProtectionFilter>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          ...payload,
        },
      }));
    },
    setWtpNtfState: (payload: Partial<Notification>) => {
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          notification: {
            ...state.tmWebthreatprotections.notification,
            ...payload,
          },
        },
      }));
    },
    importWtpApprvoedSender: (emails: string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const separator = ';';
      const {
        tmWebthreatprotections: { wtpApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const importEmails = emails.filter((email) => email.length > 0);
      const existedEntries = wtpApprovedSenderEmailEntries
        .split(separator)
        .filter((email: string) => email.length > 0);

      // process exceedLimit
      if (
        importEmails.length + existedEntries.length >
        limitation.approvedListMaxCount
      ) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
        return;
      }
      // add emails
      currentStore.addWtpApprovedSender(importEmails);
    },
    addWtpApprovedSender: (email: string | string[]) => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const includeDomain = false;
      const separator = ';';
      const {
        tmWebthreatprotections: { wtpApprovedSenderEmailEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const isSupportWildcard = true;
      const { errList, entriesStr, exceedListLimit } = addEmails(
        email,
        wtpApprovedSenderEmailEntries,
        includeDomain,
        limitation.singleApprovedListMaxCount,
        separator,
        limitation.approvedListMaxCount,
        isSupportWildcard
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedSenderEmailEntries: entriesStr,
        },
      }));
      // process exceedLimit
      if (exceedListLimit) {
        showAddApprovedSenderExceedListLimitNotification(
          limitation.approvedListMaxCount
        );
      }
      // process error list
      if (errList.length) {
        console.error('addWtpApprovedSender error list:', errList);
        showAddApprovedSenderListErrNotification(errList);
      }
    },
    removeWtpApprovedSender: (email: string) => {
      const deleteEmail = email.trim();
      if (deleteEmail === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmWebthreatprotections: { wtpApprovedSenderEmailEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteEmail,
        wtpApprovedSenderEmailEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedSenderEmailEntries: result,
        },
      }));
    },
    addWtpApprovedUrls: (url: string | string[]) => {
      let urls = Array.isArray(url) ? url : [url];
      urls = urls.map((item) => {
        return removeProtocol(item);
      });
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpApprovedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const entries = wtpApprovedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      const {
        entries: newEntries,
        errList,
        exceedLimit,
      } = addUrlToList(
        urls,
        entries,
        limitation.approvedListMaxCount,
        limitation.singleApprovedListMaxCount
      );
      // setPolicy new entries
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedUrlListEntries: newEntries.join(separator),
        },
      }));
      // process exceedLimit
      if (exceedLimit) {
        showAddUrlExceedListLimitNotification(limitation.approvedListMaxCount);
      }
      // process error list
      if (errList.length) {
        console.error('addWtpApprovedUrl error list:', errList);
        showAddUrlsErrNotification(errList);
      }
    },
    removeWtpApprovedUrl: (url: string) => {
      const deleteUrl = url.trim();
      if (deleteUrl === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmWebthreatprotections: { wtpApprovedUrlListEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteUrl,
        wtpApprovedUrlListEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedUrlListEntries: result,
        },
      }));
    },
    addWtpBlockedUrls: (url: string | string[]) => {
      let urls = Array.isArray(url) ? url : [url];
      urls = urls.map((item) => {
        return removeProtocol(item);
      });
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpBlockedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const entries = wtpBlockedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      const {
        entries: newEntries,
        errList,
        exceedLimit,
      } = addUrlToList(
        urls,
        entries,
        limitation.blockedUrlMaxCount,
        limitation.singleBlockedListMaxCount
      );
      // setPolicy new entries
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpBlockedUrlListEntries: newEntries.join(separator),
        },
      }));
      // process exceedLimit
      if (exceedLimit) {
        showAddUrlExceedListLimitNotification(limitation.blockedUrlMaxCount);
      }
      // process error list
      if (errList.length) {
        console.log('addWtpBlockedUrl error list:', errList);
        showAddUrlsErrNotification(errList);
      }
    },
    removeWtpBlockedUrl: (url: string) => {
      const deleteUrl = url.trim();
      if (deleteUrl === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmWebthreatprotections: { wtpBlockedUrlListEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(deleteUrl, wtpBlockedUrlListEntries, separator);
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpBlockedUrlListEntries: result,
        },
      }));
    },
    addWtpApprovedHeader: (header: ApprovedHeader) => {
      const currentStore = getAll();
      const existedEntries = wtpApprovedHeaderListSelector(currentStore);
      existedEntries.push(header);
      // setPolicy state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedHeaderList: JSON.stringify(existedEntries),
        },
      }));
    },
    removeWtpApprovedHeader: (header: ApprovedHeader) => {
      const currentStore = getAll();
      const existedEntries = wtpApprovedHeaderListSelector(currentStore);
      const newEntries = removeHeader(header, existedEntries);
      // setPolicy state
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpApprovedHeaderList: JSON.stringify(newEntries),
        },
      }));
    },
    importWtpApprovedUrls: (url: string[]) => {
      const urls = url.filter((item) => item !== '');
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpApprovedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const entries = wtpApprovedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      if (urls.length + entries.length > limitation.approvedListMaxCount) {
        showAddUrlExceedListLimitNotification(limitation.approvedListMaxCount);
      } else {
        currentStore.addWtpApprovedUrls(urls);
      }
    },
    importWtpBlockedUrls: (url: string[]) => {
      const urls = url.filter((item) => item !== '');
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpBlockedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const urlListType = 0;
      const entries = wtpBlockedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      if (urls.length + entries.length > limitation.blockedUrlMaxCount) {
        showAddUrlExceedListLimitNotification(
          limitation.blockedUrlMaxCount,
          urlListType
        );
      } else {
        currentStore.addWtpBlockedUrls(urls);
      }
    },
    validateWebReputation: () => {
      const currentState = getPolicy();
      const currentStore = getAll();
      const actionData = wtpActionDataSelector(currentStore);
      currentStore.replaceInvalidAction(actionData);

      const filter = currentState.tmWebthreatprotections;
      const result = {
        [PolicyFilters.tmWebthreatprotections]: {},
      };
      const { policyType } = filter;
      const { notifyMessageMaxLength, notifySubjectMaxLength } =
        currentStore.config.limitation;
      let checkResult: any;
      // check action
      const actionKey = 'wtpActionType';
      const suspiciousTagKey = 'wtpSuspiciousTag';
      const actionType = filter[actionKey];
      const suspiciousTagValue = filter[suspiciousTagKey];
      checkResult = checkActionByType(suspiciousTagValue, actionType);
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmWebthreatprotections] = {
          [suspiciousTagKey]: checkResult,
        };
        return result;
      }
      // check notification
      checkResult = checkNotification(
        filter.notification,
        notifyMessageMaxLength,
        notifySubjectMaxLength
      );
      if (checkResult) {
        // return directly when meet an error
        result[PolicyFilters.tmWebthreatprotections] = checkResult;
        return result;
      }
      // check token
      if (
        ![PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType)
      ) {
        checkToken({
          keyValue: filter.wtpReplacementText,
          keyName: 'wtpReplacementText',
        });
        if (checkResult) {
          // return directly when meet an error
          result[PolicyFilters.tmWebthreatprotections] = checkResult;
          return result;
        }
      }
      return;
    },
    addWtpTocApprovedUrls: (url: string | string[]) => {
      let urls = Array.isArray(url) ? url : [url];
      urls = urls.map((item) => {
        return removeProtocol(item);
      });
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpTocApprovedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const entries = wtpTocApprovedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      const {
        entries: newEntries,
        errList,
        exceedLimit,
      } = addUrlToList(
        urls,
        entries,
        limitation.tocApprovedListMaxCount,
        limitation.singleTocApprovedListMaxCount
      );
      // setPolicy new entries
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpTocApprovedUrlListEntries: newEntries.join(separator),
        },
      }));
      // process exceedLimit
      if (exceedLimit) {
        showAddUrlExceedListLimitNotification(
          limitation.tocApprovedListMaxCount
        );
      }
      // process error list
      if (errList.length) {
        console.error('addWtpTocApprovedUrl error list:', errList);
        showAddUrlsErrNotification(errList);
      }
    },
    removeWtpTocApprovedUrls: (url: string) => {
      const deleteUrl = url.trim();
      if (deleteUrl === '') {
        return;
      }
      const currentState = getPolicy();
      const {
        tmWebthreatprotections: { wtpTocApprovedUrlListEntries },
      } = currentState;
      const separator = ';';
      const result = removeItem(
        deleteUrl,
        wtpTocApprovedUrlListEntries,
        separator
      );
      setPolicy((state: ExchangeOnlinePolicyItem) => ({
        ...state,
        tmWebthreatprotections: {
          ...state.tmWebthreatprotections,
          wtpTocApprovedUrlListEntries: result,
        },
      }));
    },
    importWtpTocApprovedUrls: (url: string[]) => {
      const urls = url.filter((item) => item !== '');
      const separator = ';';
      const currentState = getPolicy();
      const currentStore = getAll();
      const {
        tmWebthreatprotections: { wtpTocApprovedUrlListEntries },
      } = currentState;
      const {
        config: { limitation },
      } = currentStore;
      const entries = wtpTocApprovedUrlListEntries
        .split(separator)
        .filter((entry: string) => entry !== '');
      if (urls.length + entries.length > limitation.tocApprovedListMaxCount) {
        showAddUrlExceedListLimitNotification(
          limitation.tocApprovedListMaxCount
        );
      } else {
        currentStore.addWtpTocApprovedUrls(urls);
      }
    },
  };
}
